/**
 * Was die eben festgeschriebene Rechnung am Projekt bewirkt hat — und die zwei
 * Fragen, die nur der Mensch beantworten kann (Benjamin, 15.10.2026).
 *
 * Von selbst geschieht (Server, `rechnung_folgen`): das Projekt kommt auf
 * „🔵 rechnung", und ein Angebot, aus dem die Rechnung erstellt wurde, gilt als
 * angenommen. Hier wird nur noch gefragt:
 *
 *  1. **Angenommen?** Liegt am Projekt ein Angebot beim Kunden und die Rechnung
 *     entstand nicht daraus — „Hat der Kunde AN… angenommen?". Eine kleine
 *     Beratungsrechnung neben einem offenen Angebot sagt über dessen Annahme
 *     nichts, deshalb keine Automatik.
 *  2. **Rest oder Abrechnung?** Bleibt aus dem Angebot etwas „noch nicht
 *     abgerechnet" — wird das noch in Rechnung gestellt, oder war das die
 *     Abrechnung? Im zweiten Fall bekommt die Rechnung den Vermerk
 *     `schliesst_auftrag`: der Auftragswert sinkt auf die Summe der Rechnungen,
 *     auf dem Papier bleibt sie eine „Rechnung".
 */
import { useState } from "react";

import { useUpdateInvoiceRecord } from "../../hooks/useInvoices";
import {
  errorText, euro,
  type InvoiceRegisterItem, type OffenesAngebot, type ProjektWirkung, type RechnungFolgen,
} from "../../lib/invoices";

type Props = { wirkung: ProjektWirkung | null; folgen: RechnungFolgen | null | undefined; item: InvoiceRegisterItem };

export default function FestschreibenFolgen({ wirkung: start, folgen, item }: Props) {
  const [wirkung, setWirkung] = useState(start);
  const istRechnung = item.kind !== "quote";
  if (!wirkung && !folgen) return null;
  return (
    <div className="invoice-projektwirkung">
      <p className="invoice-kicker">Wirkung auf das Projekt</p>
      {wirkung && <Zahlen wirkung={wirkung} />}
      {folgen?.status_gesetzt && <p className="is-ok">Projektstatus steht jetzt auf {folgen.status_gesetzt}.</p>}
      {folgen?.angebot_angenommen && (
        <p className="is-ok">Angebot {folgen.angebot_angenommen} ist als angenommen eingetragen.</p>
      )}
      {istRechnung && folgen && folgen.angebote_offen.length > 0 && <AnnahmeFrage angebote={folgen.angebote_offen} />}
      {istRechnung && wirkung && item.kind === "invoice" && (wirkung.nicht_abgerechnet > 0.005 || wirkung.abgeschlossen_durch_vermerk) && (
        <RestFrage item={item} wirkung={wirkung} onWirkung={setWirkung} />
      )}
    </div>
  );
}

function Zahlen({ wirkung }: { wirkung: ProjektWirkung }) {
  return (
    <>
      {wirkung.geaendert ? (
        <p>
          Auftragswert <strong>{euro(wirkung.alt)} → {euro(wirkung.neu)}</strong>
          <small> ({wirkung.herkunft_text})</small>
        </p>
      ) : (
        <p>Auftragswert unverändert bei <strong>{euro(wirkung.auftragswert)}</strong> <small>({wirkung.herkunft_text})</small></p>
      )}
      <p>
        Gestellt {euro(wirkung.gestellt)}
        {wirkung.nicht_abgerechnet > 0 ? <> · noch nicht abgerechnet <strong>{euro(wirkung.nicht_abgerechnet)}</strong></> : null}
        {wirkung.offen > 0 ? <> · offen {euro(wirkung.offen)}</> : null}
      </p>
    </>
  );
}

/** „Hat der Kunde AN… angenommen?" — je offenem Angebot ein Knopf. */
function AnnahmeFrage({ angebote }: { angebote: OffenesAngebot[] }) {
  const update = useUpdateInvoiceRecord();
  const [angenommen, setAngenommen] = useState<string[]>([]);
  const [nein, setNein] = useState(false);
  const offen = angebote.filter((a) => !angenommen.includes(a.nummer));
  if (nein && angenommen.length === 0) return <p><small>Angebot bleibt offen.</small></p>;
  if (offen.length === 0 || (nein && angenommen.length > 0)) {
    return <p className="is-ok">Angebot {angenommen.join(", ")} ist als angenommen eingetragen.</p>;
  }
  const annehmen = (a: OffenesAngebot) => update.mutate(
    { id: a.id, patch: { status: "angenommen" } },
    { onSuccess: () => setAngenommen((v) => [...v, a.nummer]) },
  );
  return (
    <div className="invoice-projektwirkung-frage">
      <strong>
        {offen.length === 1
          ? `Hat der Kunde Angebot ${offen[0].nummer} angenommen?`
          : "Hat der Kunde eines dieser Angebote angenommen?"}
      </strong>
      <span>Spätestens mit der Rechnung sollte die Zusage eingetragen sein.</span>
      <div className="invoice-abschluss-optionen">
        {offen.map((a) => (
          <button key={a.id} type="button" className="invoice-button primary" disabled={update.isPending} onClick={() => annehmen(a)}>
            {offen.length === 1 ? "Ja, angenommen" : `${a.nummer} · ${euro(a.netto)} angenommen`}
          </button>
        ))}
        <button type="button" className="invoice-button" disabled={update.isPending} onClick={() => setNein(true)}>Noch nicht</button>
      </div>
      {update.error && <p className="invoice-projektwirkung-fehler">{errorText(update.error)}</p>}
    </div>
  );
}

/** „Wird der Rest noch abgerechnet?" — Nein setzt den Vermerk an der Rechnung. */
function RestFrage({ item, wirkung, onWirkung }: {
  item: InvoiceRegisterItem; wirkung: ProjektWirkung; onWirkung: (w: ProjektWirkung) => void;
}) {
  const update = useUpdateInvoiceRecord();
  const [antwort, setAntwort] = useState<"" | "folgt">("");
  const setzen = (an: boolean) => update.mutate(
    { id: item.id, patch: { schliesst_auftrag: an } },
    {
      onSuccess: (neu) => {
        const w = (neu as { projekt_wirkung?: ProjektWirkung | null }).projekt_wirkung;
        if (w) onWirkung(w);
      },
    },
  );
  if (wirkung.abgeschlossen_durch_vermerk) {
    return (
      <div className="invoice-projektwirkung-frage is-erledigt">
        <span className="is-ok">{item.nummer} schließt den Auftrag ab — der Rest aus dem Angebot entfällt.</span>
        <button type="button" className="invoice-linkbutton" disabled={update.isPending} onClick={() => setzen(false)}>Rückgängig</button>
      </div>
    );
  }
  if (antwort === "folgt") {
    return <p><small>Der Rest von {euro(wirkung.nicht_abgerechnet)} bleibt als „noch nicht abgerechnet" am Projekt.</small></p>;
  }
  return (
    <div className="invoice-projektwirkung-frage">
      <strong>Wird der Rest von {euro(wirkung.nicht_abgerechnet)} noch in Rechnung gestellt?</strong>
      <span>Ist das die Abrechnung, sinkt der Auftragswert auf {euro(wirkung.gestellt)} — auf dem Papier bleibt es eine Rechnung.</span>
      <div className="invoice-abschluss-optionen">
        <button type="button" className="invoice-button" disabled={update.isPending} onClick={() => setAntwort("folgt")}>Ja, Rest folgt</button>
        <button type="button" className="invoice-button primary" disabled={update.isPending} onClick={() => setzen(true)}>
          {update.isPending ? "Speichert …" : "Nein, das ist die Abrechnung"}
        </button>
      </div>
      {update.error && <p className="invoice-projektwirkung-fehler">{errorText(update.error)}</p>}
    </div>
  );
}
